import type { ProjectMenuItemResponse } from '@protoss/shared';
import { Tooltip } from '@protoss/ui';
import * as React from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { AppNav } from '../components/AppNav';
import { ProjectAdminInfo } from '../components/organization/DomainMembersPanel';
import { ProjectIntegrationsPanel } from '../components/organization/ProjectIntegrationsPanel';
import { AppShell, MainContentShell } from '../components/shell';
import type { RightRailItem } from '../components/shell/AppShell';
import { useAppContext } from '../contexts/AppContext';
import { useAuth } from '../contexts/AuthContext';
import { useLayout, useLayoutConfig } from '../contexts/LayoutContext';
import { useWorkspace } from '../contexts/WorkspaceContext';
import {
  useChatSession,
  type UseChatSessionReturn,
} from '../features/ai-chat/hooks/useChatSession';
import { getProjectChatPolicy } from '../features/ai-chat/types/chatPolicy';
import { ConsumptionPage } from '../features/consumption/components/ConsumptionPage';
import type { ContextDocsPaneHandle } from '../features/context-docs/components/ContextDocsPane';
import { CustomerDetailPage } from '../features/customers/components/CustomerDetailPage';
import { CustomerListPage } from '../features/customers/components/CustomerListPage';
import { DataCatalogPage } from '../features/data-catalog/components/DataCatalogPage';
import type { DataCatalogSettingsPaneHandle } from '../features/data-catalog/components/DataCatalogSettingsPane';
import { CommitsPage } from '../features/ingestion/components/CommitsPage';
import { SourceSystemPanel } from '../features/ingestion/components/SourceSystemPanel';
import type { SourceSystemSettingsPaneHandle } from '../features/ingestion/components/SourceSystemSettingsPane';
import type { SourceType } from '../features/ingestion/types/source-system';
import { JobsPage } from '../features/jobs/components/JobsPage';
import { useJobLogs } from '../features/jobs/hooks/useJobLogs';
import { LiveMeetingPane } from '../features/live-meeting/components/LiveMeetingPane';
import { MeetingsPanel } from '../features/meetings/components/MeetingsPanel';
import { useProjectMenu } from '../features/project-menu/hooks/useProjectMenu';
import { useProjectRightRail } from '../features/project-menu/hooks/useProjectRightRail';
import { resolveProjectMenuIcon } from '../features/project-menu/icons/iconRegistry';
import type { PanelContext } from '../features/project-menu/panels/panelRegistry';
import { PANEL_REGISTRY } from '../features/project-menu/panels/panelRegistry';
import type { ProjectResourcesPanelHandle } from '../features/project-resources/components/ProjectResourcesPanel';
import { PageTitle } from '../hooks/usePageTitle';
import { useWorkspaceAdmin } from '../hooks/useWorkspaceAdmin';
import './home-page.css';
import './project-page.css';

// ────────────────────────────────────────────────────────────────────────────
// Mock data - replace with real queries when project detail API is wired up
// ────────────────────────────────────────────────────────────────────────────

const MOCK_STATS = [
  { label: 'Open Tasks', value: '12' },
  { label: 'In Progress', value: '3' },
  { label: 'Completed', value: '47' },
  { label: 'Milestones', value: '2' },
];

const MOCK_ACTIVITY = [
  {
    id: '1',
    text: 'Task "Update API schema" moved to In Progress',
    meta: '2 hours ago · Alex R.',
  },
  {
    id: '2',
    text: 'New milestone "Q2 Release" created',
    meta: '5 hours ago · Jamie L.',
  },
  {
    id: '3',
    text: 'Task "Fix authentication bug" completed',
    meta: 'Yesterday · Sam T.',
  },
  {
    id: '4',
    text: 'Project settings updated',
    meta: '2 days ago · Admin',
  },
];

// ────────────────────────────────────────────────────────────────────────────
// Project context pane - project-specific navigation menu
// ────────────────────────────────────────────────────────────────────────────

interface ProjectContextMenuProps {
  projectName: string;
  selectedItemId: string;
  getItemHref: (id: string) => string;
  items: ProjectMenuItemResponse[];
}

function ProjectContextMenu({
  projectName,
  selectedItemId,
  getItemHref,
  items,
}: ProjectContextMenuProps) {
  // Group items by menuGroupKey, preserving API order.
  // Disabled items (not entitled) are hidden for MVP.
  const groups = React.useMemo(() => {
    const groupMap = new Map<string, ProjectMenuItemResponse[]>();
    for (const item of items) {
      if (!item.isEnabled) continue;
      const existing = groupMap.get(item.menuGroupKey) ?? [];
      existing.push(item);
      groupMap.set(item.menuGroupKey, existing);
    }
    return [...groupMap.entries()].map(([key, groupItems]) => ({ key, items: groupItems }));
  }, [items]);

  return (
    <div className="project-context-menu">
      <div className="project-context-menu__header">
        <Tooltip.Simple label={projectName}>
          <span className="project-context-menu__title">{projectName}</span>
        </Tooltip.Simple>
      </div>

      <nav className="project-context-menu__nav" aria-label="Project navigation">
        {groups.map(({ key: groupKey, items: groupItems }) => (
          <div key={groupKey} className="project-context-menu__section">
            {groupKey !== 'overview' && (
              <div className="project-context-menu__section-label">
                {groupKey.charAt(0).toUpperCase() + groupKey.slice(1)}
              </div>
            )}
            {groupItems.map((item) => (
              <Link
                key={item.key}
                to={getItemHref(item.key)}
                className="project-context-menu__item"
                data-selected={selectedItemId === item.key ? 'true' : 'false'}
              >
                <span className="project-context-menu__item-icon">
                  {resolveProjectMenuIcon(item.iconKey)}
                </span>
                <span className="project-context-menu__item-label">{item.label}</span>
              </Link>
            ))}
          </div>
        ))}
      </nav>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// DataOperationProjectShell - full project experience for data_operation module
// ────────────────────────────────────────────────────────────────────────────

function DataOperationProjectShell() {
  const navigate = useNavigate();

  const { workspace, isLoadingDomains } = useWorkspace();
  const { currentProject, breadcrumb } = useAppContext();
  const { appUser } = useAuth();
  const isWorkspaceAdmin = useWorkspaceAdmin(workspace);

  // True when the current user is listed as an admin of this specific project
  const isProjectAdmin =
    !!appUser && (currentProject?.admins.some((a) => a.uuid === appUser.uuid) ?? false);
  const isProjectManager =
    !!appUser && (currentProject?.managers.some((m) => m.uuid === appUser.uuid) ?? false);
  const canManageIntegrations = isWorkspaceAdmin || isProjectAdmin;
  const canManageResources = isWorkspaceAdmin || isProjectAdmin || isProjectManager;

  // ── Project-scoped AI chat session ──────────────────────────────────────
  // useChatSession is lifted here (project level) so the session persists
  // across all project menu navigation, and the SessionControls can live in
  // the RightUtilityPane header while AiChatPane is the body content.
  //
  // Default context is project-scoped only. pageScope (activeMenuItem) is
  // NOT stored in the session context; it is injected per-run as a secondary
  // context hint by AiChatPane.handleSend().
  const chatDefaultContext = React.useMemo(
    () => ({ projectUuid: currentProject?.uuid }),
    [currentProject?.uuid]
  );
  const chatSession = useChatSession(chatDefaultContext);

  // Role-based chat policy.
  // TODO: Resolve the user's precise project role (manager/viewer/collaborator)
  // from /api/project/:uuid/my-role or equivalent once that endpoint exists.
  // Currently we derive admin status only; non-admins get full access pending
  // real role resolution. Collaborator restriction will auto-apply once role
  // data is wired in.
  const projectRole = isWorkspaceAdmin || isProjectAdmin ? 'admin' : null;
  const chatPolicy = getProjectChatPolicy(
    projectRole as Parameters<typeof getProjectChatPolicy>[0]
  );

  const { projectUuid, section } = useParams<{ projectUuid: string; section?: string }>();
  const activeMenuItem = section ?? 'overview';

  // Fetch the dynamic project menu from the API (entitlement + role already resolved server-side)
  const { items: menuItems, isLoading: isMenuLoading } = useProjectMenu(projectUuid);

  // Systems menu group items - derived from API response so new source-system items
  // are automatically picked up without a frontend code change.
  const SYSTEMS_GROUP_ITEMS = React.useMemo(
    () => new Set(menuItems.filter((i) => i.menuGroupKey === 'systems').map((i) => i.key)),
    [menuItems]
  );
  const isSystemsGroup = SYSTEMS_GROUP_ITEMS.has(activeMenuItem);

  // Static layout config: collapse left nav, show context pane
  useLayoutConfig({
    collapseLeftNav: true,
    showContextPane: true,
    showRightRail: true,
  });

  // Dynamic right rail - toggle based on active menu group
  const { rightPaneVisible, rightPaneMode } = useLayout();

  // Left nav - shared primary navigation (same as HomePage)
  const leftNav = (
    <AppNav
      selected={null}
      isWorkspaceAdmin={isWorkspaceAdmin}
      onAdministrationClick={() => navigate('/administration')}
    />
  );

  const isSystemOrOperatePage = ['repos', 'commits', 'boards', 'pipelines', 'jobs'].includes(
    activeMenuItem
  );

  // Incremented when settings pane saves - triggers SourceSystemPanel to re-fetch
  const [settingsVersion, setSettingsVersion] = React.useState(0);

  // Sync log state - driven by SourceSystemPanel via onRunStateChange callback
  const [syncLogRunUuid, setSyncLogRunUuid] = React.useState<string | null>(null);
  const [syncLogIsActive, setSyncLogIsActive] = React.useState(false);

  const handleRunStateChange = React.useCallback((runUuid: string | null, isActive: boolean) => {
    setSyncLogRunUuid(runUuid);
    setSyncLogIsActive((prev) => {
      // When a run transitions active → inactive, reload the resources panel so
      // newly-detected platforms appear immediately without a manual refresh.
      if (prev && !isActive) {
        resourcesPaneRef.current?.reload();
      }
      return isActive;
    });
  }, []);

  // Settings tab is role-gated - handled by the API (requiresRole = 'manager')
  // so no client-side canSeeSettings variable is needed.

  // Ref + state for the settings pane Edit button in the rail pane header
  const settingsPaneRef = React.useRef<SourceSystemSettingsPaneHandle>(null);
  const [settingsIsEditing, setSettingsIsEditing] = React.useState(false);

  // Ref + state for context docs pane New button in the rail pane header
  const contextDocsPaneRef = React.useRef<ContextDocsPaneHandle>(null);
  const [contextDocsIsEditing, setContextDocsIsEditing] = React.useState(false);

  // Ref + state for data catalog settings pane Edit button in the rail pane header
  const dataCatalogSettingsPaneRef = React.useRef<DataCatalogSettingsPaneHandle>(null);
  const [dataCatalogSettingsIsEditing, setDataCatalogSettingsIsEditing] = React.useState(false);

  // Ref for resources pane Add button in the rail pane header
  const resourcesPaneRef = React.useRef<ProjectResourcesPanelHandle>(null);

  // Context pane - project-specific menus (DB-driven, entitlement resolved server-side)
  const contextPane =
    currentProject && !isMenuLoading ? (
      <ProjectContextMenu
        projectName={currentProject.name}
        selectedItemId={activeMenuItem}
        getItemHref={(id) => `/do/project/${projectUuid}/${id}`}
        items={menuItems}
      />
    ) : currentProject ? (
      <div className="project-context-menu">
        <div className="project-context-menu__header">
          <Tooltip.Simple label={currentProject.name}>
            <span className="project-context-menu__title">{currentProject.name}</span>
          </Tooltip.Simple>
        </div>
      </div>
    ) : null;

  // Source type - derived from the active menu item, used by settings/sync panels
  const sourceType: SourceType =
    activeMenuItem === 'repos' || activeMenuItem === 'commits'
      ? 'repo'
      : activeMenuItem === 'boards'
        ? 'board'
        : activeMenuItem === 'data-catalog'
          ? 'data-catalog'
          : 'pipeline';

  const isSourceSystemPage = isSystemsGroup;

  // Sync log job logs - fetched only when a run UUID is known
  const {
    logs: syncLogs,
    hasMore: syncLogsHasMore,
    loading: syncLogsLoading,
    loadMore: syncLogsLoadMore,
  } = useJobLogs(currentProject?.uuid ?? '', syncLogRunUuid, syncLogIsActive);

  // ── DB-driven right rail ───────────────────────────────────────────────────
  // Items, entitlement state, and role requirements come from the API.
  // Content is wired locally via the panel registry.

  const { items: rightRailData } = useProjectRightRail(projectUuid);

  // Determine the active left-nav menu group so we can filter applicableMenuGroups.
  const activeMenuGroupKey = React.useMemo(
    () => menuItems.find((i) => i.key === activeMenuItem)?.menuGroupKey ?? 'overview',
    [menuItems, activeMenuItem]
  );

  // Compile panel context - all state needed by any panel renderer.
  const panelCtx: PanelContext = {
    projectUuid,
    projectReady: !!currentProject,
    activeMenuItem,
    sourceType,
    canManageIntegrations,
    canManageResources,
    chatSession,
    chatPolicy,
    syncLogs,
    syncLogsHasMore,
    syncLogsLoading,
    syncLogsLoadMore,
    contextDocsPaneRef,
    contextDocsIsEditing,
    setContextDocsIsEditing,
    settingsPaneRef,
    settingsIsEditing,
    setSettingsIsEditing,
    onSettingsSaved: () => setSettingsVersion((v) => v + 1),
    dataCatalogSettingsPaneRef,
    dataCatalogSettingsIsEditing,
    setDataCatalogSettingsIsEditing,
    onDataCatalogSettingsSaved: () => setSettingsVersion((v) => v + 1),
    resourcesPaneRef,
  };

  // Build the RightRailItem array from DB data + panel registry.
  // The DB provides: iconKey, label (base), displayOrder, isEnabled, applicableMenuGroups.
  // The panel registry provides: content, headerAction, isEditing, labelSuffix, enabled.
  const rightRailItems: RightRailItem[] = rightRailData
    .filter((item) => {
      // Drop items the workspace is not entitled to
      if (!item.isEnabled) return false;
      // Drop items that don't apply to the current left-nav menu group context
      if (item.applicableMenuGroups && !item.applicableMenuGroups.includes(activeMenuGroupKey)) {
        return false;
      }
      return true;
    })
    .sort((a, b) => a.displayOrder - b.displayOrder)
    .flatMap((item) => {
      const renderer = PANEL_REGISTRY[item.panelKey];
      if (!renderer) return [];
      const rendered = renderer(panelCtx);
      // renderer can signal additional runtime disabled state (e.g. project not yet loaded)
      if (rendered.enabled === false) return [];
      const label = rendered.labelSuffix ? `${item.label} · ${rendered.labelSuffix}` : item.label;
      return [
        {
          id: item.panelKey,
          label,
          icon: resolveProjectMenuIcon(item.iconKey),
          isEditing: rendered.isEditing,
          enabled: true,
          content: rendered.content,
          headerAction: rendered.headerAction,
          headerTitle: rendered.headerTitle,
          noPadding: rendered.noPadding,
        } satisfies RightRailItem,
      ];
    });

  // Main pane - reuses MainContentShell, content varies by activeMenuItem
  // Source-system pages (repos/boards/pipelines) bypass MainContentShell -
  // SourceSystemPanel owns its own header (breadcrumb + actions).
  // TODO: each menu item should render its own dedicated content view
  const main = (
    <>
      {isLoadingDomains && (
        <div className="main-pane-header">
          <h1 className="main-pane-title">Loading…</h1>
        </div>
      )}

      {!isLoadingDomains && !currentProject && (
        <div className="main-pane-header">
          <h1 className="main-pane-title">Project not found</h1>
        </div>
      )}

      {!isLoadingDomains && currentProject && isSourceSystemPage && (
        <div className="ss-panel-scroll">
          <PageTitle
            title={`${currentProject.name} - ${activeMenuItem.charAt(0).toUpperCase() + activeMenuItem.slice(1).replace('-', ' ')}`}
          />
          {activeMenuItem === 'repos' ? (
            <SourceSystemPanel
              projectUuid={currentProject.uuid}
              sourceType="repo"
              breadcrumb={breadcrumb}
              canEdit={canManageIntegrations}
              refreshKey={settingsVersion}
              onRunStateChange={handleRunStateChange}
              onSetDraftExplicitContext={
                rightPaneVisible && rightPaneMode === 'chat'
                  ? chatSession.setDraftExplicitContext
                  : undefined
              }
            />
          ) : activeMenuItem === 'commits' ? (
            <CommitsPage
              projectUuid={currentProject.uuid}
              breadcrumb={breadcrumb}
              canEdit={canManageIntegrations}
              refreshKey={settingsVersion}
              onRunStateChange={handleRunStateChange}
            />
          ) : activeMenuItem === 'boards' ? (
            <SourceSystemPanel
              projectUuid={currentProject.uuid}
              sourceType="board"
              breadcrumb={breadcrumb}
              canEdit={canManageIntegrations}
              refreshKey={settingsVersion}
              onRunStateChange={handleRunStateChange}
            />
          ) : activeMenuItem === 'data-catalog' ? (
            <DataCatalogPage
              projectUuid={currentProject.uuid}
              breadcrumb={breadcrumb}
              canEdit={canManageIntegrations}
              refreshKey={settingsVersion}
            />
          ) : activeMenuItem === 'consumption' ? (
            <ConsumptionPage projectUuid={currentProject.uuid} breadcrumb={breadcrumb} />
          ) : (
            <SourceSystemPanel
              projectUuid={currentProject.uuid}
              sourceType="pipeline"
              breadcrumb={breadcrumb}
              canEdit={canManageIntegrations}
              refreshKey={settingsVersion}
              onRunStateChange={handleRunStateChange}
            />
          )}
        </div>
      )}

      {!isLoadingDomains && currentProject && activeMenuItem === 'jobs' && (
        <div className="ss-panel-scroll">
          <PageTitle title={`${currentProject.name} - Jobs`} />
          <JobsPage projectUuid={currentProject.uuid} breadcrumb={breadcrumb} />
        </div>
      )}

      {!isLoadingDomains && currentProject && activeMenuItem === 'meetings' && (
        <div className="ss-panel-scroll">
          <PageTitle title={`${currentProject.name} - Meetings`} />
          <MeetingsPanel
            refreshKey={settingsVersion}
            syncError={null}
            onResetSyncError={() => {}}
          />
        </div>
      )}

      {!isLoadingDomains &&
        currentProject &&
        !isSourceSystemPage &&
        activeMenuItem !== 'jobs' &&
        activeMenuItem !== 'meetings' && (
          <MainContentShell
            title={currentProject.name}
            breadcrumb={breadcrumb}
            canEdit={isWorkspaceAdmin && !isSystemOrOperatePage}
            onEdit={() => navigate(`/do/project/${currentProject.uuid}/edit`)}
          >
            {activeMenuItem === 'integrations' && canManageIntegrations ? (
              <ProjectIntegrationsPanel
                projectUuid={currentProject.uuid}
                canManage={canManageIntegrations}
              />
            ) : (
              /* Default: project overview - mock data placeholder */
              <div className="project-overview">
                {/* Summary stats */}
                <div>
                  <p className="project-section-label">Summary</p>
                  <div className="project-stats">
                    {MOCK_STATS.map((stat) => (
                      <div key={stat.label} className="project-stat-card">
                        <span className="project-stat-card__value">{stat.value}</span>
                        <span className="project-stat-card__label">{stat.label}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Domain and description */}
                <div>
                  <div className="entity-info__section">
                    <h3 className="entity-info__section-title">Domain</h3>
                    <p className="entity-info__stat">{currentProject.domainName}</p>
                  </div>

                  <div className="entity-info__section">
                    <h3 className="entity-info__section-title">Description</h3>
                    {currentProject.description ? (
                      <p className="entity-info__description">{currentProject.description}</p>
                    ) : (
                      <p className="entity-info__empty">No description yet.</p>
                    )}
                  </div>
                </div>

                {/* Administrators */}
                <div className="entity-info__section">
                  <h3 className="entity-info__section-title">Administrators</h3>
                  <ProjectAdminInfo admins={currentProject.admins} />
                </div>

                {/* Recent activity (mock) */}
                {/* TODO: replace with real activity feed from API */}
                <div className="project-activity">
                  <p className="project-section-label">Recent Activity</p>
                  {MOCK_ACTIVITY.length === 0 ? (
                    <p className="project-activity__empty">No recent activity.</p>
                  ) : (
                    <div className="project-activity__list">
                      {MOCK_ACTIVITY.map((item) => (
                        <div key={item.id} className="project-activity__item">
                          <div className="project-activity__item-dot" aria-hidden="true" />
                          <div className="project-activity__item-body">
                            <p className="project-activity__item-text">{item.text}</p>
                            <p className="project-activity__item-meta">{item.meta}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </MainContentShell>
        )}
    </>
  );

  return (
    <AppShell
      leftNav={leftNav}
      contextPane={contextPane}
      main={main}
      rightRailItems={rightRailItems}
    />
  );
}

/**
 * ProjectPage - dedicated project workspace page
 *
 * Rendered at /project/:projectUuid.
 *
 * Layout:
 *   LeftNavPane  - shared primary navigation (Organization, Administration)
 *   ContextPane  - project-specific navigation menu (placeholder/mock sections)
 *   MainPane     - project overview content via MainContentShell (placeholder/mock)
 *   RightPane    - utility panels: chat, timeline, resources (placeholder)
 *
 * TODOs (replace mock data with real implementations):
 *   - Wire context menu items to real content views
 *   - Replace MOCK_STATS with real project metrics from API
 *   - Replace MOCK_ACTIVITY with real activity feed from API
 *   - Replace right pane placeholders with AI chat, timeline, resources
 *   - Persist / restore activeMenuItem via URL param
 */
/**
 * ProductProjectShell - project shell for the product module.
 *
 * Uses the same DB-driven project menu as DataOperationProjectShell, but with
 * module_scope = 'product' filtering applied server-side. Currently surfaces:
 *   - overview   (all modules)
 *   - meeting_simulation (product only) - live meeting transcript + AI recs
 *   - members, settings (all modules)
 */
function ProductProjectShell() {
  const navigate = useNavigate();
  const { workspace, isLoadingWorkspace } = useWorkspace();
  const { currentProject, breadcrumb } = useAppContext();
  const isWorkspaceAdmin = useWorkspaceAdmin(workspace);
  const { projectUuid, section } = useParams<{ projectUuid: string; section?: string }>();
  const activeMenuItem = section ?? 'overview';

  const [selectedPersonaUuid, setSelectedPersonaUuid] = React.useState<string | null>(null);
  const [creatingCustomer, setCreatingCustomer] = React.useState(false);

  React.useEffect(() => {
    setSelectedPersonaUuid(null);
    setCreatingCustomer(false);
  }, [activeMenuItem]);

  const { items: menuItems, isLoading: isMenuLoading } = useProjectMenu(projectUuid);
  const { items: rightRailData } = useProjectRightRail(projectUuid);
  const activeMenuGroupKey = React.useMemo(
    () => menuItems.find((i) => i.key === activeMenuItem)?.menuGroupKey ?? 'overview',
    [menuItems, activeMenuItem]
  );
  const contextDocsPaneRef = React.useRef<ContextDocsPaneHandle>(null);
  const settingsPaneRef = React.useRef<SourceSystemSettingsPaneHandle>(null);
  const dataCatalogSettingsPaneRef = React.useRef<DataCatalogSettingsPaneHandle>(null);
  const resourcesPaneRef = React.useRef<ProjectResourcesPanelHandle>(null);

  useLayoutConfig({
    collapseLeftNav: true,
    showContextPane: true,
    showRightRail: true,
  });

  if (isLoadingWorkspace) return null;

  const panelCtx: PanelContext = {
    projectUuid,
    projectReady: !!currentProject,
    activeMenuItem,
    sourceType: 'pipeline',
    canManageIntegrations: isWorkspaceAdmin,
    canManageResources: isWorkspaceAdmin,
    chatSession: {} as UseChatSessionReturn,
    chatPolicy: getProjectChatPolicy(null),
    syncLogs: [],
    syncLogsHasMore: false,
    syncLogsLoading: false,
    syncLogsLoadMore: () => {},
    contextDocsPaneRef,
    contextDocsIsEditing: false,
    setContextDocsIsEditing: () => {},
    settingsPaneRef,
    settingsIsEditing: false,
    setSettingsIsEditing: () => {},
    onSettingsSaved: () => {},
    dataCatalogSettingsPaneRef,
    dataCatalogSettingsIsEditing: false,
    setDataCatalogSettingsIsEditing: () => {},
    onDataCatalogSettingsSaved: () => {},
    resourcesPaneRef,
  };

  const rightRailItems: RightRailItem[] = rightRailData
    .filter((item) => {
      if (!item.isEnabled) return false;
      if (item.applicableMenuGroups && !item.applicableMenuGroups.includes(activeMenuGroupKey)) {
        return false;
      }
      return true;
    })
    .sort((a, b) => a.displayOrder - b.displayOrder)
    .flatMap((item) => {
      const renderer = PANEL_REGISTRY[item.panelKey];
      if (!renderer) return [];
      const rendered = renderer(panelCtx);
      if (rendered.enabled === false) return [];
      const label = rendered.labelSuffix ? `${item.label} · ${rendered.labelSuffix}` : item.label;
      return [
        {
          id: item.panelKey,
          label,
          icon: resolveProjectMenuIcon(item.iconKey),
          isEditing: rendered.isEditing,
          enabled: true,
          content: rendered.content,
          headerAction: rendered.headerAction,
          headerTitle: rendered.headerTitle,
          noPadding: rendered.noPadding,
        } satisfies RightRailItem,
      ];
    });

  const leftNav = (
    <AppNav
      selected={null}
      isWorkspaceAdmin={isWorkspaceAdmin}
      onAdministrationClick={() => navigate('/administration')}
    />
  );

  const contextPane =
    currentProject && !isMenuLoading ? (
      <ProjectContextMenu
        projectName={currentProject.name}
        selectedItemId={activeMenuItem}
        getItemHref={(id) => `/do/project/${projectUuid}/${id}`}
        items={menuItems}
      />
    ) : currentProject ? (
      <div className="project-context-menu">
        <div className="project-context-menu__header">
          <Tooltip.Simple label={currentProject.name}>
            <span className="project-context-menu__title">{currentProject.name}</span>
          </Tooltip.Simple>
        </div>
      </div>
    ) : null;

  const main = (
    <>
      {!currentProject && (
        <div className="main-pane-header">
          <h1 className="main-pane-title">Project not found</h1>
        </div>
      )}

      {currentProject && activeMenuItem === 'meeting_simulation' && (
        <div className="ss-panel-scroll">
          <LiveMeetingPane />
        </div>
      )}

      {currentProject && activeMenuItem === 'meetings' && (
        <div className="ss-panel-scroll">
          <MeetingsPanel refreshKey={0} syncError={null} onResetSyncError={() => {}} />
        </div>
      )}

      {currentProject && activeMenuItem === 'customers' && selectedPersonaUuid === null && (
        <CustomerListPage
          projectUuid={projectUuid!}
          canEdit={isWorkspaceAdmin}
          creating={creatingCustomer}
          onCreateStart={() => setCreatingCustomer(true)}
          onCreateCancel={() => setCreatingCustomer(false)}
          onSelectCustomer={(uuid: string) => setSelectedPersonaUuid(uuid)}
        />
      )}

      {currentProject && activeMenuItem === 'customers' && selectedPersonaUuid !== null && (
        <CustomerDetailPage
          projectUuid={projectUuid!}
          customerUuid={selectedPersonaUuid}
          canEdit={isWorkspaceAdmin}
        />
      )}

      {currentProject &&
        activeMenuItem !== 'meeting_simulation' &&
        activeMenuItem !== 'meetings' &&
        activeMenuItem !== 'customers' && (
          <MainContentShell
            title={currentProject.name}
            breadcrumb={breadcrumb}
            canEdit={isWorkspaceAdmin}
            onEdit={() => navigate(`/do/project/${currentProject.uuid}/edit`)}
          >
            <div className="entity-info__section">
              <h3 className="entity-info__section-title">Domain</h3>
              <p className="entity-info__stat">{currentProject.domainName}</p>
            </div>
            {currentProject.description && (
              <div className="entity-info__section">
                <h3 className="entity-info__section-title">Description</h3>
                <p className="entity-info__description">{currentProject.description}</p>
              </div>
            )}
          </MainContentShell>
        )}
    </>
  );

  return (
    <AppShell
      leftNav={leftNav}
      contextPane={contextPane}
      main={main}
      rightRailItems={rightRailItems}
    />
  );
}

/**
 * ProjectPage - dispatches to the correct project shell based on the project's module.
 *
 * Reads currentProject.moduleKey (set from project.module_key in the DB) and renders
 * the appropriate experience. Falls back to DataOperationProjectShell for unknown modules.
 */
export function ProjectPage() {
  const { currentProject } = useAppContext();
  const moduleKey = currentProject?.moduleKey ?? 'data_operation';
  if (moduleKey === 'product') return <ProductProjectShell />;
  return <DataOperationProjectShell />;
}
